import { computed, unref, watch } from 'vue';
import {
  useHead,
  useSeoMeta,
  useRuntimeConfig,
  useRoute,
  useRequestURL,
} from '#app';

import { useGlobalStore } from '@/stores/global';

export const useSeo = (input) => {
  const route = useRoute();
  const config = useRuntimeConfig();
  const requestUrl = useRequestURL();

  const globalStore = useGlobalStore();
  const clientUrl = (config.public.clientUrl || requestUrl.origin).replace(
    /\/+$/,
    '',
  );

  const titleBase = computed(() => unref(input.title) || '');
  const description = computed(() => unref(input.description) || '');
  const keywords = computed(() => unref(input.keywords) || '');
  const robots = computed(() => unref(input.robots) || 'index, follow');
  const image = computed(() => unref(input.image) || null);
  const catalogTitle = computed(() => unref(input.catalogTitle) || '');
  const catalogDescription = computed(
    () => unref(input.catalogDescription) || '',
  );

  // flush: 'sync' matters here: the default ('pre') defers the callback to
  // Vue's job scheduler, which is not guaranteed to flush again before Nuxt
  // serializes the SSR response (see the analogous finding in
  // useCatalogData.js re: catalogPayload). 'sync' runs the callback
  // synchronously, in the same tick as the reactive change caused by the
  // catalog fetch resolving, so the store is updated before anything that
  // renders after this component reads it during SSR.
  watch(
    [catalogTitle, catalogDescription],
    ([newTitle, newDesc]) => {
      if (newTitle) globalStore.setCatalogPageTitle(newTitle);
      if (newDesc) globalStore.setCatalogPageMetaDescription(newDesc);
    },
    { immediate: true, flush: 'sync' },
  );

  const purePath = computed(() => {
    let p = unref(input.path) || route.path;
    p = p.replace(/^\/(en|uk)(\/|$)/, '/').replace(/\/$/, '') || '/';
    if (p === '/home' || p === '/pages/home') {
      return '/';
    }

    return p;
  });

  const finalTitle = computed(() => {
    return `${titleBase.value}`.trim();
  });
  const cleanPath = computed(() => {
    return purePath.value === '/' ? '/' : purePath.value;
  });
  const canonicalUrl = computed(() => {
    const lang = route.params.lang;
    const langPrefix = lang && lang !== 'uk' ? `/${lang}` : '';
    return `${clientUrl}${langPrefix}${cleanPath.value}`;
  });

  const alternateLinks = computed(() => {
    const cleanPath = purePath.value === '/' ? '' : purePath.value;
    return [
      {
        key: 'alternate-uk-UA',
        rel: 'alternate',
        hreflang: 'uk-UA',
        href: `${clientUrl}${cleanPath}`,
      },
      {
        key: 'alternate-en',
        rel: 'alternate',
        hreflang: 'en-UA',
        href: `${clientUrl}/en${cleanPath}`,
      },
      {
        key: 'alternate-x-default',
        rel: 'alternate',
        hreflang: 'x-default',
        href: `${clientUrl}${cleanPath}`,
      },
    ];
  });

  const fullImage = computed(() => {
    if (!image.value) return undefined;
    return image.value.startsWith('http')
      ? image.value
      : `${clientUrl}${image.value}`;
  });

  useSeoMeta({
    title: finalTitle,
    description,
    keywords,
    robots,
    ogTitle: finalTitle,
    ogDescription: description,
    ogUrl: canonicalUrl,
    ogImage: fullImage,
    twitterCard: 'summary_large_image',
  });

  useHead(() => ({
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...alternateLinks.value,
    ],
  }));
};
